import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { IoMdWarning } from 'react-icons/io';
import dayjs from 'dayjs';

import InfoTooltip from './InfoTooltip';
import { useGetEventRecurringSeries } from '../../../events/hooks';
import Spinner, { SpinnerType } from '../spinner/Spinner';
import {
  RECURRING_FREQUENCY_LABELS,
  RecurringFrequency
} from '../../../events/types';
import Modal, { ModalType } from '../modal/Modal';
import Button, { ButtonType } from '../button/Button';

interface Props {
  eventId: string;
  navigateToRecurringSettings: () => void;
}

const computeFrequency = (
  frequency: RecurringFrequency,
  startDate: string,
  numberOfEvents: number
) => {
  const frequencyText = RECURRING_FREQUENCY_LABELS[frequency];
  const startDay = dayjs(startDate).format('dddd');
  // TODO: It hurts, i18n
  return `${frequencyText} on ${startDay}s (${numberOfEvents}x)`;
};

const RecurringEvent = ({ navigateToRecurringSettings, eventId }: Props) => {
  const [isRequirementsModalOpen, setIsRequirementsModalOpen] = useState(false);
  const { values } = useFormikContext<any>();
  const { data, isLoading } = useGetEventRecurringSeries(
    { eventId },
    { enabled: !!eventId && !!values.seriesId }
  );

  const toggleRequirementsModal = () => {
    setIsRequirementsModalOpen((prevState) => !prevState);
  };

  const handleRecurringEventClick = () => {
    if (
      !eventId ||
      !values.title ||
      !values.photo?.url ||
      !values.description ||
      !values.address ||
      !values.coordinates
    ) {
      return toggleRequirementsModal();
    }

    return navigateToRecurringSettings();
  };

  return (
    <>
      <div className="w-full mr-10 pt-7 text-base text-gray-500 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/img/icons/sync.svg" className="mr-2 w-[20px]" />
          {` Repeat`}
          <InfoTooltip
            id="recurring-event-tooltip"
            message="Repeating an event creates up to 10 linked drafts with all details and settings copied. Each draft in the series can be edited individually."
          />
        </div>
        {isLoading ? (
          <Spinner type={SpinnerType.PRIMARY} />
        ) : (
          <button
            type="button"
            className="py-1 pl-2 text-primary-500 text-base font-semibold"
            onClick={handleRecurringEventClick}
          >
            {data
              ? computeFrequency(
                  data.frequency,
                  data.startDate,
                  data.eventIds.length
                )
              : 'Does not repeat'}
          </button>
        )}
      </div>
      <Modal
        visible={isRequirementsModalOpen}
        onClose={toggleRequirementsModal}
        type={ModalType.SMALL}
        autoHeight
        title="Required Fields Missing"
        icon={
          <div className="bg-yellow-100 flex-1 h-full flex items-center justify-center text-yellow-800">
            <IoMdWarning size={20} />
          </div>
        }
        renderFooter={() => (
          <Button
            type={ButtonType.Primary}
            onClick={toggleRequirementsModal}
            newRadius
          >
            Ok
          </Button>
        )}
      >
        <div className="p-4">
          <p className="text-base font-normal text-neutral-600">
            To use the repeat feature the event must first have the required
            fields completed: Event Poster, Name, Location and Description.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default RecurringEvent;
